import { IconName } from 'components/Icon'
import { isPlainObject } from 'lodash'
import { action, computed, observable, reaction, makeObservable } from 'mobx'
import { merge, Subscription } from 'rxjs'
import { filter, switchMap, tap } from 'rxjs/operators'
import { toStream } from 'utils/mobx-util'
import { createObjectStorage, ObjectStorage, Storage } from './storage'

/**
 * Constructor argument for filters available
 */
export type FiltersDefinition<TFilterMapping> = {
  [TKey in keyof TFilterMapping]: Omit<
    IFilterRegistration<TKey, TFilterMapping[TKey]>,
    'key' | 'conflictsWith'
  > & {
    /**
     * Keys of filters this one cannot be applied with; declaring one side is enough.
     */
    readonly conflictsWith?: ReadonlyArray<
      Extract<keyof TFilterMapping, string>
    >
  }
}

/**
 * A registered definition for a filter and how to create one.
 */
export interface IFilterRegistration<TKey, TValue> {
  readonly key: TKey
  readonly title: string
  readonly icon?: IconName
  readonly persist?: boolean
  readonly implicit?: boolean
  readonly isVisible?: () => boolean
  readonly conflictsWith?: ReadonlyArray<string>
  readonly factory: (key: TKey, title: string) => IFilterViewModel<TKey, TValue>
}

/**
 * A registered definition including if filter has already been added
 */
export interface IFilterOption<TKey, TValue> {
  readonly key: TKey
  readonly title: string
  readonly icon?: IconName
  readonly implicit?: boolean
  readonly current: IFilterViewModel<TKey, TValue> | null
  readonly isVisible: () => boolean
  /**
   * An applied filter this one conflicts with, if any.
   */
  readonly conflict: IFilterViewModel<string, unknown> | null
}

/**
 * Item source to use.
 */
export type FilterItemSource<V> = () => Promise<FilterItem<V>[]>

export const FilterItemSource = {
  /**
   * Static item source.
   *
   * @param items
   */
  static<V>(items: FilterItem<V>[]): FilterItemSource<V> {
    return async () => {
      return items
    }
  },
}

/**
 * Item from the filter source.
 */
export interface FilterItem<V> {
  value: V
  title: string
  description?: string
}

/**
 * The resulting filters where an entry can be null or undefined.
 */
export type FiltersResult<TFilters> = {
  [P in keyof TFilters]?: TFilters[P] | null
}

/**
 * View model options for creating a FiltersViewModel
 */
export interface FiltersViewModelOptions<
  TFilters extends Record<string, unknown>,
> {
  definitions: FiltersDefinition<TFilters>
  filterStorage?: FilterStorage
}

/**
 * View model to manage available and applied filters.
 */
export class FiltersViewModel<TFilters extends Record<string, unknown>> {
  private readonly filterStorage?: FilterStorage

  private disposeReaction?: () => void
  private subscription?: Subscription

  /**
   * Stream for the currently enabled filters. Emits current value on subscribe.
   */
  readonly appliedFilters$ = toStream(() => this.appliedFilters, true)

  /**
   * Explicitly enabled filters.
   */
  @observable explicitFilterVms = observable<IFilterViewModel<string, unknown>>(
    [],
  )

  /**
   * Implicitly enabled filters.
   */
  @observable implicitFilterVms = observable<IFilterViewModel<string, unknown>>(
    [],
  )

  /**
   * All filters available to us.
   */
  @observable availableFilterRegistrations: Array<
    IFilterRegistration<string, unknown>
  > = []

  /**
   * Pipeline that clears explicit filters when they stop editing in
   * an invalid state.
   */
  private readonly clearInvalidOnStopEditing$ = toStream(() =>
    this.explicitFilterVms.slice(),
  ).pipe(
    switchMap((filterVms) =>
      merge(
        ...filterVms.map((vm) =>
          toStream(() => vm.editing).pipe(
            filter((editing) => !editing && !vm.valid),
            tap(() => this.clearFilter(vm.key)),
          ),
        ),
      ),
    ),
  )

  constructor({
    definitions,
    filterStorage,
  }: FiltersViewModelOptions<TFilters>) {
    makeObservable(this)
    this.filterStorage = filterStorage

    for (const key in definitions) {
      const def = definitions[key]
      this.availableFilterRegistrations.push({
        key,
        title: def.title,
        factory: def.factory as any,
        icon: def.icon,
        implicit: def.implicit,
        persist: def.persist,
        isVisible: def.isVisible,
        conflictsWith: def.conflictsWith,
      })
    }
    this.addImplicitFilters()
  }

  /**
   * Available explicit filter options and if they have been added
   */
  @computed
  get availableFilters(): Array<IFilterOption<string, unknown>> {
    return this.availableFilterRegistrations
      .filter((a) => !a.implicit)
      .map((f) => ({
        key: f.key,
        title: f.title,
        icon: f.icon,
        implicit: f.implicit,
        current: this.getFilter(f.key),
        isVisible: f.isVisible || (() => true),
        conflict: this.findAppliedConflict(f.key),
      }))
  }

  /**
   * Finds an applied filter that conflicts with the given one.
   */
  private findAppliedConflict(key: string) {
    const applied = [...this.explicitFilterVms, ...this.implicitFilterVms]
    return applied.find((filter) => this.areInConflict(key, filter.key)) ?? null
  }

  /**
   * Whether either filter declares a conflict with the other.
   */
  private areInConflict(left: string, right: string) {
    const conflictsOf = (key: string) =>
      this.availableFilterRegistrations.find((r) => r.key === key)
        ?.conflictsWith ?? []
    return (
      conflictsOf(left).includes(right) || conflictsOf(right).includes(left)
    )
  }

  /**
   * The filters being applied
   */
  @computed
  get appliedFilters(): FiltersResult<TFilters> {
    const result: any = {}
    for (const filter of this.explicitFilterVms) {
      result[filter.key] = filter.value
    }
    for (const filter of this.implicitFilterVms) {
      result[filter.key] = filter.value
    }
    return result
  }

  /**
   * Count of explicit filters with a value
   */
  @computed
  get appliedFilterCount() {
    return this.explicitFilterVms.length
  }

  /**
   * Adds a filter.
   */
  @action.bound
  addFilter<TKey extends keyof TFilters>(key: TKey) {
    const [filter, filterDefinition] = this.createFilter(key)

    if (this.explicitFilterVms.some((f) => f.key === filter.key)) {
      throw new Error('Cannot apply same filter more than once')
    }
    if (this.implicitFilterVms.some((f) => f.key === filter.key)) {
      throw new Error('Cannot apply same filter more than once')
    }

    // If our filter is implicit, don't start editing.
    if (filterDefinition.implicit) {
      this.implicitFilterVms.push(filter as any)
      return
    }

    filter.startEditing()
    this.explicitFilterVms.push(filter as any)

    if (filter.refresh) {
      filter.refresh().then()
    }
  }

  /**
   * Removes a filter.
   */
  @action.bound
  removeFilter<TKey extends keyof TFilters>(key: TKey) {
    const idx = this.availableFilterRegistrations.findIndex(
      (f) => f.key === key,
    )

    if (idx !== -1) {
      this.availableFilterRegistrations.splice(idx, 1)
    }
  }

  /**
   * Sets editing on an appliedFilter
   */
  editFilter<TKey extends keyof TFilters>(key: TKey) {
    const filter = this.explicitFilterVms.find((f) => f.key === key)
    filter?.startEditing()
  }

  /**
   * Getter for an appliedFilter
   */
  getFilter<TKey extends keyof TFilters>(key: TKey) {
    return (
      [...this.explicitFilterVms, ...this.implicitFilterVms]?.find(
        (f) => f.key === key,
      ) ?? null
    )
  }

  /**
   * Makes a filter implicit.
   */
  @action.bound
  makeFilterImplicit<TKey extends keyof TFilters>(key: TKey) {
    const registration = this.availableFilterRegistrations.find(
      (afr) => afr.key === key,
    )

    if (!registration) {
      return
    }

    this.removeFilter(key)
    this.availableFilterRegistrations.push({ ...registration, implicit: true })
    this.loadFilters()
  }

  /**
   * Clears explicit filters
   */
  @action.bound
  clearExplicitFilters() {
    this.explicitFilterVms = observable([])
  }

  /**
   * Clears the given filter
   */
  @action.bound
  clearFilter<TKey extends keyof TFilters>(key: TKey) {
    const explFilter = this.explicitFilterVms.find((f) => f.key === key)
    if (explFilter) {
      explFilter.clearValue()
      this.explicitFilterVms.remove(explFilter)
      return
    }

    const implFilter = this.implicitFilterVms.find((f) => f.key === key)
    if (implFilter) {
      implFilter.clearValue()
      implFilter.stopEditing()
    }
  }

  /**
   * Loads initial filters and stores changes to filters as user edits them
   */
  @action
  activate() {
    // Load filters
    this.loadFilters()

    // Persist filters as they change
    this.disposeReaction = reaction(
      () => this.serializeFilters(),
      (serialized) => this.filterStorage?.save(serialized),
      {
        fireImmediately: false,
      },
    )

    this.subscription = this.clearInvalidOnStopEditing$.subscribe()
  }

  @action
  deactivate() {
    this.disposeReaction?.()
    this.subscription?.unsubscribe()
  }

  /**
   * Serializes filters so they may be stored
   */
  private serializeFilters(): Record<string, unknown> {
    const obj: Record<string, unknown> = {}

    for (const filter of [
      ...this.explicitFilterVms,
      ...this.implicitFilterVms,
    ]) {
      const filterDefinition = this.availableFilterRegistrations.find(
        (d) => d.key === filter.key,
      )

      if (!filterDefinition?.persist) {
        continue
      }

      const serialized = filter.serialize()

      if (serialized !== DO_NOT_SERIALIZE) {
        obj[filter.key] = serialized
      }
    }

    return obj
  }

  /**
   * Loads stored filters, deserializes them, and applies them.
   */
  @action
  private loadFilters() {
    const loaded = this.filterStorage?.load()
    if (!loaded) {
      return
    }

    this.clearAllFilters()
    for (const filterRegistration of this.availableFilterRegistrations) {
      if (!(filterRegistration.key in loaded)) {
        continue
      }

      if (!filterRegistration.persist) {
        continue
      }

      const serialized = loaded[filterRegistration.key]

      const filter =
        this.implicitFilterVms.find((r) => r.key === filterRegistration.key) ??
        filterRegistration.factory(
          filterRegistration.key,
          filterRegistration.title,
        )

      filter.deserialize(serialized)

      if (filter.valid && filterRegistration.implicit) {
        this.implicitFilterVms.remove(filter)
        this.implicitFilterVms.push(filter)
      } else if (filter.valid) {
        this.explicitFilterVms.push(filter)
      }
    }
  }

  /**
   * Creates a new filter view model from the filter registration.
   *
   * @param key
   * @returns
   */
  private createFilter<TKey extends keyof TFilters>(key: TKey) {
    const filterDefinition = this.availableFilterRegistrations.find(
      (d) => d.key === key,
    )
    if (!filterDefinition) {
      throw new Error(`Missing a filter definition for key ${key.toString()}`)
    }

    return [
      filterDefinition.factory(key as string, filterDefinition.title),
      filterDefinition,
    ] as const
  }

  /**
   * Adds all implicitly defined filters.
   */
  private addImplicitFilters() {
    for (const registration of this.availableFilterRegistrations) {
      if (registration.implicit) {
        this.addFilter(registration.key)
      }
    }
  }

  /**
   * Clears all filters.
   */
  private clearAllFilters() {
    this.explicitFilterVms = observable([])
    this.implicitFilterVms = observable([])
    this.addImplicitFilters()
  }
}

/**
 * Contract that a filter view model must satisfy.
 */
export interface IFilterViewModel<TKey, TValue> {
  /**
   * The field being filtered on.
   */
  readonly key: TKey
  /**
   * The title of the filter.
   */
  readonly title: string
  /**
   * Value of the filter.
   */
  readonly value: TValue | null
  /**
   * Whether the filter is being edited or not
   */
  readonly editing: boolean
  /**
   * Whether the filter is valid or not
   */
  readonly valid: boolean
  /**
   * Method to prepare filter value for display purposes
   */
  toString: () => string
  /**
   * Method to clear the filter value
   */
  clearValue: () => void
  /**
   * Starts editing the filter with the specified key if it has been applied.
   */
  startEditing: () => void
  /**
   * Stops editing the filter with the specified key if it has been applied.
   */
  stopEditing: () => void
  /**
   * Serialize the filter into a form that can be stored in localStorage.
   */
  serialize: () => unknown
  /**
   * Deserialize the filter into a form that can be set as a value.
   */
  deserialize: (value: unknown) => void

  /**
   * Initialize the filter
   * @returns
   */
  refresh?: () => Promise<void>
}

/**
 * Class to handle storing and retrieving filters from local storage.
 */
export class FilterStorage {
  private readonly key = `WorklayerFilters.${this.namespace}`
  private readonly storage: ObjectStorage

  constructor(
    storage: Storage,
    private namespace: string,
  ) {
    this.storage = createObjectStorage(storage)
  }

  load = (): Record<string, unknown> => {
    const res = this.storage.getItem(this.key)

    if (isPlainObject(res)) {
      return res as Record<string, unknown>
    }

    return {}
  }

  save = (filters: Record<string, unknown>) => {
    this.storage.setItem(this.key, filters)
  }
}

/**
 * Delegate to create a filter storage for a namespace.
 */
export type FilterStorageFactory = (namespace: string) => FilterStorage

/**
 * Returned by FilterViewModel to tell us when we don't want to serialize that value
 */
export const DO_NOT_SERIALIZE = Symbol('DO_NOT_SERIALIZE')
